import { useState, useMemo, useEffect } from "react";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Pencil, Trash2, Microscope, Search, Ruler, FlaskConical, Stethoscope, ChevronDown, ChevronUp } from "lucide-react";
import { format } from "date-fns";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useDataStale } from "@/contexts/DataStaleContext";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface BiopsyRecord {
  type: "biopsy_report";
  structure_type?: "biopsy_report" | "macroscopic_findings" | "microscopic_findings" | "diagnosis" | "immunohistochemistry" | "metrics";
  date: string;
  is_main_record: boolean;
  name?: string | null;
  _index?: number;
  // Campos para laudo principal
  exam_type?: string | null;
  specimen_type?: string | null;
  specimen_site?: string | null;
  doctor_name?: string | null;
  doctor_crm?: string | null;
  exam_date?: string | null;
  // Campos para achados macroscópicos
  finding_description?: string | null;
  finding_type?: string | null;
  dimensions?: string | null;
  color?: string | null;
  consistency?: string | null;
  // Campos para achados microscópicos
  histological_pattern?: string | null;
  cellular_features?: string | null;
  stromal_features?: string | null;
  // Campos para diagnóstico
  diagnosis_text?: string | null;
  diagnosis_type?: string | null;
  site?: string | null;
  grade?: string | null;
  stage?: string | null;
  // Campos para imunohistoquímica
  marker_name?: string | null;
  marker_result?: string | null;
  intensity?: string | null;
  percentage?: number | null;
  // Campos para métricas
  metric_name?: string;
  metric_name_slug?: string;
  value?: number;
  unit?: string;
}

interface SmartfileSession {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_summary?: string | null;
  created_at: string;
  structured_records?: any[] | null;
  patient_id: string | null;
  status: string | null;
}

interface SmartfileBiopsyDetailsProps {
  session: SmartfileSession;
  onRefresh: () => void;
  onRecordsChange?: (updatedRecords: any[]) => void;
  expandAllByDefault?: boolean;
}

export function SmartfileBiopsyDetails({ session, onRefresh, onRecordsChange, expandAllByDefault = false }: SmartfileBiopsyDetailsProps) {
  const { markBothStale } = useDataStale();
  const isMobile = useIsMobile();
  const [editingIndex, setEditingIndex] = useState<{ section: string; index: number } | null>(null);
  const [editingMainRecord, setEditingMainRecord] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedRecords, setEditedRecords] = useState<Map<string, BiopsyRecord[]>>(new Map());
  const [expandedDateGroups, setExpandedDateGroups] = useState<Map<string, boolean>>(new Map());

  // Buscar o registro principal do laudo
  const mainRecord = useMemo(() => {
    return (session.structured_records || []).find((record: any) =>
      typeof record === 'object' &&
      record !== null &&
      'type' in record &&
      record.type === 'biopsy_report' &&
      record.structure_type === 'biopsy_report' &&
      ((record as any).is_main_record === true || (record as any).is_main_record === 'true')
    ) as BiopsyRecord | undefined;
  }, [session.structured_records]);

  // Separar registros por structure_type
  const allBiopsyRecords = useMemo(() => {
    return (session.structured_records || [])
      .filter((record: any): record is BiopsyRecord =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'biopsy_report'
      );
  }, [session.structured_records]);

  // Agrupar por structure_type
  const biopsyMainRecords = useMemo(() => {
    return allBiopsyRecords.filter(r => r.structure_type === 'biopsy_report' && !r.is_main_record);
  }, [allBiopsyRecords]);

  const macroscopicMainRecord = useMemo(() => {
    return allBiopsyRecords.find(r => r.structure_type === 'macroscopic_findings' && r.is_main_record);
  }, [allBiopsyRecords]);

  const macroscopicSubRecords = useMemo(() => {
    return allBiopsyRecords.filter(r => r.structure_type === 'macroscopic_findings' && !r.is_main_record);
  }, [allBiopsyRecords]);

  const microscopicMainRecord = useMemo(() => {
    return allBiopsyRecords.find(r => r.structure_type === 'microscopic_findings' && r.is_main_record);
  }, [allBiopsyRecords]);

  const microscopicSubRecords = useMemo(() => {
    return allBiopsyRecords.filter(r => r.structure_type === 'microscopic_findings' && !r.is_main_record);
  }, [allBiopsyRecords]);

  const diagnosisMainRecord = useMemo(() => {
    return allBiopsyRecords.find(r => r.structure_type === 'diagnosis' && r.is_main_record);
  }, [allBiopsyRecords]);

  const diagnosisSubRecords = useMemo(() => {
    return allBiopsyRecords.filter(r => r.structure_type === 'diagnosis' && !r.is_main_record);
  }, [allBiopsyRecords]);

  const immunohistochemistryMainRecord = useMemo(() => {
    return allBiopsyRecords.find(r => r.structure_type === 'immunohistochemistry' && r.is_main_record);
  }, [allBiopsyRecords]);

  const immunohistochemistrySubRecords = useMemo(() => {
    return allBiopsyRecords.filter(r => r.structure_type === 'immunohistochemistry' && !r.is_main_record);
  }, [allBiopsyRecords]);

  const metricsRecords = useMemo(() => {
    return allBiopsyRecords.filter(r => r.structure_type === 'metrics');
  }, [allBiopsyRecords]);

  const [expandedSections, setExpandedSections] = useState<Map<string, boolean>>(new Map([
    ['macroscopic', expandAllByDefault],
    ['microscopic', expandAllByDefault],
    ['diagnosis', expandAllByDefault],
    ['immunohistochemistry', expandAllByDefault],
    ['metrics', expandAllByDefault],
  ]));

  // Inicializar editedRecords quando os dados mudarem
  useEffect(() => {
    const records = new Map<string, BiopsyRecord[]>();
    records.set('biopsy', [...biopsyMainRecords]);
    records.set('macroscopic', [...macroscopicSubRecords]);
    records.set('microscopic', [...microscopicSubRecords]);
    records.set('diagnosis', [...diagnosisSubRecords]);
    records.set('immunohistochemistry', [...immunohistochemistrySubRecords]);
    records.set('metrics', [...metricsRecords]);
    setEditedRecords(records);
  }, [biopsyMainRecords, macroscopicSubRecords, microscopicSubRecords, diagnosisSubRecords, immunohistochemistrySubRecords, metricsRecords]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Map(prev);
      next.set(section, !(next.get(section) ?? false));
      return next;
    });
  };

  const handleSave = async (section: string, index: number) => {
    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];
      const sectionRecords = editedRecords.get(section) || [];
      const recordToSave = sectionRecords[index];

      if (!recordToSave) return;

      // Encontrar o índice real no array completo
      let sectionCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'biopsy_report') {
          const recordStructureType = (record as any).structure_type;
          const isMainRecord = (record as any).is_main_record === true || (record as any).is_main_record === 'true';
          
          let matches = false;
          if (section === 'biopsy' && recordStructureType === 'biopsy_report' && !isMainRecord) {
            matches = true;
          } else if (section === 'macroscopic' && recordStructureType === 'macroscopic_findings' && !isMainRecord) {
            matches = true;
          } else if (section === 'microscopic' && recordStructureType === 'microscopic_findings' && !isMainRecord) {
            matches = true;
          } else if (section === 'diagnosis' && recordStructureType === 'diagnosis' && !isMainRecord) {
            matches = true;
          } else if (section === 'immunohistochemistry' && recordStructureType === 'immunohistochemistry' && !isMainRecord) {
            matches = true;
          } else if (section === 'metrics' && recordStructureType === 'metrics') {
            matches = true;
          }

          if (matches) {
            if (sectionCount === index) {
              realIndex = i;
              break;
            }
            sectionCount++;
          }
        }
      }

      if (realIndex !== -1) {
        updatedRecords[realIndex] = recordToSave;

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        if (session.patient_id) {
          await markBothStale(session.patient_id);
        }

        toast.success("✅ Registro atualizado com sucesso!");
        setEditingIndex(null);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (section: string, index: number) => {
    try {
      const updatedRecords = [...(session.structured_records || [])];

      // Encontrar o índice real no array completo
      let sectionCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'biopsy_report') {
          const recordStructureType = (record as any).structure_type;
          const isMainRecord = (record as any).is_main_record === true || (record as any).is_main_record === 'true';
          
          let matches = false;
          if (section === 'biopsy' && recordStructureType === 'biopsy_report' && !isMainRecord) {
            matches = true;
          } else if (section === 'macroscopic' && recordStructureType === 'macroscopic_findings' && !isMainRecord) {
            matches = true;
          } else if (section === 'microscopic' && recordStructureType === 'microscopic_findings' && !isMainRecord) {
            matches = true;
          } else if (section === 'diagnosis' && recordStructureType === 'diagnosis' && !isMainRecord) {
            matches = true;
          } else if (section === 'immunohistochemistry' && recordStructureType === 'immunohistochemistry' && !isMainRecord) {
            matches = true;
          } else if (section === 'metrics' && recordStructureType === 'metrics') {
            matches = true;
          }

          if (matches) {
            if (sectionCount === index) {
              realIndex = i;
              break;
            }
            sectionCount++;
          }
        }
      }

      if (realIndex !== -1) {
        updatedRecords.splice(realIndex, 1);

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        if (session.patient_id) {
          await markBothStale(session.patient_id);
        }

        toast.success("✅ Registro excluído com sucesso!");
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      toast.error("❌ Erro ao excluir registro");
    }
  };

  const updateEditedRecord = (section: string, index: number, field: string, value: any) => {
    const sectionRecords = editedRecords.get(section) || [];
    const updated = [...sectionRecords];
    updated[index] = { ...updated[index], [field]: value };
    const newRecords = new Map(editedRecords);
    newRecords.set(section, updated);
    setEditedRecords(newRecords);
  };

  // Função auxiliar para agrupar registros por data
  const groupRecordsByDate = (records: BiopsyRecord[]) => {
    return records.reduce((acc, record, index) => {
      const date = record.date || session.created_at;
      const dateObj = new Date(date);

      if (isNaN(dateObj.getTime())) {
        const invalidKey = 'invalid-date';
        if (!acc[invalidKey]) {
          acc[invalidKey] = {
            displayKey: 'Data Inválida',
            records: []
          };
        }
        acc[invalidKey].records.push({ ...record, _index: index });
        return acc;
      }

      const monthYearKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
      const displayKey = `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;

      if (!acc[monthYearKey]) {
        acc[monthYearKey] = {
          displayKey,
          records: []
        };
      }
      acc[monthYearKey].records.push({ ...record, _index: index });
      return acc;
    }, {} as Record<string, { displayKey: string; records: BiopsyRecord[] }>);
  };

  // Função auxiliar para ordenar datas
  const sortDateKeys = (dateKeys: string[]) => {
    return dateKeys.sort((a, b) => {
      if (a === 'invalid-date') return 1;
      if (b === 'invalid-date') return -1;
      const dateA = new Date(a + '-01').getTime();
      const dateB = new Date(b + '-01').getTime();
      return dateB - dateA; // Mais recente primeiro
    });
  };

  // Toggle de expansão de grupos de data
  const toggleDateGroup = (dateKey: string, isOpen: boolean) => {
    setExpandedDateGroups((prev) => {
      const next = new Map(prev);
      next.set(dateKey, isOpen);
      return next;
    });
  };

  // Função para renderizar linha de tabela de achados macroscópicos
  const renderMacroscopicRow = (record: BiopsyRecord, idx: number, dateKey: string) => {
    const isEditing = editingIndex?.section === 'macroscopic' && editingIndex?.index === idx;
    const editedRecord = editedRecords.get('macroscopic')?.[idx] || record;

    return (
      <React.Fragment key={`${dateKey}-macro-${idx}`}>
        {isEditing ? (
          <tr className="border-b border-border">
            <td colSpan={6} className="p-3 bg-muted/30">
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Descrição</Label>
                  <Textarea
                    value={editedRecord.finding_description || ''}
                    onChange={(e) => updateEditedRecord('macroscopic', idx, 'finding_description', e.target.value || null)}
                    className="min-h-[80px]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Tipo</Label>
                    <Input
                      value={editedRecord.finding_type || ''}
                      onChange={(e) => updateEditedRecord('macroscopic', idx, 'finding_type', e.target.value || null)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Dimensões</Label>
                    <Input
                      value={editedRecord.dimensions || ''}
                      onChange={(e) => updateEditedRecord('macroscopic', idx, 'dimensions', e.target.value || null)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Cor</Label>
                    <Input
                      value={editedRecord.color || ''}
                      onChange={(e) => updateEditedRecord('macroscopic', idx, 'color', e.target.value || null)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Consistência</Label>
                    <Input
                      value={editedRecord.consistency || ''}
                      onChange={(e) => updateEditedRecord('macroscopic', idx, 'consistency', e.target.value || null)}
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => handleSave('macroscopic', idx)} disabled={isSaving}>
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingIndex(null)} disabled={isSaving}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : (
          <tr className="border-b border-border hover:bg-muted/30 transition-colors">
            <td className="p-3 text-sm text-muted-foreground">
              {record.date && !isNaN(new Date(record.date).getTime())
                ? format(new Date(record.date), "dd/MM/yyyy")
                : '–'}
            </td>
            <td className="p-3">
              <div className="text-sm truncate max-w-[200px]">
                {record.finding_description || '–'}
              </div>
            </td>
            <td className="p-3">
              <div className="text-sm text-muted-foreground">
                {record.finding_type || '–'}
              </div>
            </td>
            <td className="p-3">
              <div className="text-sm text-muted-foreground">
                {record.dimensions || '–'}
              </div>
            </td>
            <td className="p-3">
              <div className="text-sm text-muted-foreground">
                {record.color || '–'} / {record.consistency || '–'}
              </div>
            </td>
            <td className="p-3 text-right">
              <div className="flex gap-1 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingIndex({ section: 'macroscopic', index: idx })}
                  className="h-7 px-2"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir este registro?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete('macroscopic', idx)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Função para renderizar linha de tabela de achados microscópicos
  const renderMicroscopicRow = (record: BiopsyRecord, idx: number, dateKey: string) => {
    const isEditing = editingIndex?.section === 'microscopic' && editingIndex?.index === idx;
    const editedRecord = editedRecords.get('microscopic')?.[idx] || record;

    return (
      <React.Fragment key={`${dateKey}-micro-${idx}`}>
        {isEditing ? (
          <tr className="border-b border-border">
            <td colSpan={5} className="p-3 bg-muted/30">
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Descrição</Label>
                  <Textarea
                    value={editedRecord.finding_description || ''}
                    onChange={(e) => updateEditedRecord('microscopic', idx, 'finding_description', e.target.value || null)}
                    className="min-h-[80px]"
                  />
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label className="text-xs">Padrão Histológico</Label>
                    <Input
                      value={editedRecord.histological_pattern || ''}
                      onChange={(e) => updateEditedRecord('microscopic', idx, 'histological_pattern', e.target.value || null)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Características Celulares</Label>
                    <Input
                      value={editedRecord.cellular_features || ''}
                      onChange={(e) => updateEditedRecord('microscopic', idx, 'cellular_features', e.target.value || null)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Características do Estroma</Label>
                    <Input
                      value={editedRecord.stromal_features || ''}
                      onChange={(e) => updateEditedRecord('microscopic', idx, 'stromal_features', e.target.value || null)}
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => handleSave('microscopic', idx)} disabled={isSaving}>
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingIndex(null)} disabled={isSaving}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : (
          <tr className="border-b border-border hover:bg-muted/30 transition-colors">
            <td className="p-3 text-sm text-muted-foreground">
              {record.date && !isNaN(new Date(record.date).getTime())
                ? format(new Date(record.date), "dd/MM/yyyy")
                : '–'}
            </td>
            <td className="p-3">
              <div className="text-sm truncate max-w-[250px]">
                {record.finding_description || '–'}
              </div>
            </td>
            <td className="p-3">
              <div className="text-sm text-muted-foreground">
                {record.histological_pattern || '–'}
              </div>
            </td>
            <td className="p-3">
              <div className="text-sm text-muted-foreground truncate max-w-[200px]">
                {record.cellular_features || '–'}
              </div>
            </td>
            <td className="p-3 text-right">
              <div className="flex gap-1 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingIndex({ section: 'microscopic', index: idx })}
                  className="h-7 px-2"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir este registro?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete('microscopic', idx)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Função para renderizar linha de tabela de diagnóstico
  const renderDiagnosisRow = (record: BiopsyRecord, idx: number, dateKey: string) => {
    const isEditing = editingIndex?.section === 'diagnosis' && editingIndex?.index === idx;
    const editedRecord = editedRecords.get('diagnosis')?.[idx] || record;

    return (
      <React.Fragment key={`${dateKey}-diag-${idx}`}>
        {isEditing ? (
          <tr className="border-b border-border">
            <td colSpan={6} className="p-3 bg-muted/30">
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Diagnóstico</Label>
                  <Textarea
                    value={editedRecord.diagnosis_text || ''}
                    onChange={(e) => updateEditedRecord('diagnosis', idx, 'diagnosis_text', e.target.value || null)}
                    className="min-h-[80px]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Tipo</Label>
                    <Input
                      value={editedRecord.diagnosis_type || ''}
                      onChange={(e) => updateEditedRecord('diagnosis', idx, 'diagnosis_type', e.target.value || null)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Local</Label>
                    <Input
                      value={editedRecord.site || ''}
                      onChange={(e) => updateEditedRecord('diagnosis', idx, 'site', e.target.value || null)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Grau</Label>
                    <Input
                      value={editedRecord.grade || ''}
                      onChange={(e) => updateEditedRecord('diagnosis', idx, 'grade', e.target.value || null)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Estadiamento</Label>
                    <Input
                      value={editedRecord.stage || ''}
                      onChange={(e) => updateEditedRecord('diagnosis', idx, 'stage', e.target.value || null)}
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => handleSave('diagnosis', idx)} disabled={isSaving}>
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingIndex(null)} disabled={isSaving}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : (
          <tr className="border-b border-border hover:bg-muted/30 transition-colors">
            <td className="p-3 text-sm text-muted-foreground">
              {record.date && !isNaN(new Date(record.date).getTime())
                ? format(new Date(record.date), "dd/MM/yyyy")
                : '–'}
            </td>
            <td className="p-3">
              <div className="text-sm font-medium truncate max-w-[250px]">
                {record.diagnosis_text || '–'}
              </div>
            </td>
            <td className="p-3">
              <div className="text-sm text-muted-foreground">
                {record.diagnosis_type || '–'}
              </div>
            </td>
            <td className="p-3">
              <div className="text-sm text-muted-foreground">
                {record.site || '–'}
              </div>
            </td>
            <td className="p-3">
              <div className="text-sm text-muted-foreground">
                {record.grade || '–'} / {record.stage || '–'}
              </div>
            </td>
            <td className="p-3 text-right">
              <div className="flex gap-1 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingIndex({ section: 'diagnosis', index: idx })}
                  className="h-7 px-2"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir este registro?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete('diagnosis', idx)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Função para renderizar linha de tabela de imunohistoquímica
  const renderImmunohistochemistryRow = (record: BiopsyRecord, idx: number, dateKey: string) => {
    const isEditing = editingIndex?.section === 'immunohistochemistry' && editingIndex?.index === idx;
    const editedRecord = editedRecords.get('immunohistochemistry')?.[idx] || record;

    return (
      <React.Fragment key={`${dateKey}-ihc-${idx}`}>
        {isEditing ? (
          <tr className="border-b border-border">
            <td colSpan={6} className="p-3 bg-muted/30">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Marcador</Label>
                    <Input
                      value={editedRecord.marker_name || ''}
                      onChange={(e) => updateEditedRecord('immunohistochemistry', idx, 'marker_name', e.target.value || null)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Resultado</Label>
                    <Select
                      value={editedRecord.marker_result || ''}
                      onValueChange={(value) => updateEditedRecord('immunohistochemistry', idx, 'marker_result', value || null)}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Positivo">Positivo</SelectItem>
                        <SelectItem value="Negativo">Negativo</SelectItem>
                        <SelectItem value="Fracamente Positivo">Fracamente Positivo</SelectItem>
                        <SelectItem value="Moderadamente Positivo">Moderadamente Positivo</SelectItem>
                        <SelectItem value="Fortemente Positivo">Fortemente Positivo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Intensidade</Label>
                    <Input
                      value={editedRecord.intensity || ''}
                      onChange={(e) => updateEditedRecord('immunohistochemistry', idx, 'intensity', e.target.value || null)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Percentual (%)</Label>
                    <Input
                      type="number"
                      value={editedRecord.percentage ?? ''}
                      onChange={(e) => updateEditedRecord('immunohistochemistry', idx, 'percentage', e.target.value ? parseFloat(e.target.value) : null)}
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => handleSave('immunohistochemistry', idx)} disabled={isSaving}>
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingIndex(null)} disabled={isSaving}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : (
          <tr className="border-b border-border hover:bg-muted/30 transition-colors">
            <td className="p-3 text-sm text-muted-foreground">
              {record.date && !isNaN(new Date(record.date).getTime())
                ? format(new Date(record.date), "dd/MM/yyyy")
                : '–'}
            </td>
            <td className="p-3">
              <div className="text-sm font-medium">
                {record.marker_name || '–'}
              </div>
            </td>
            <td className="p-3">
              {record.marker_result && (
                <Badge variant={record.marker_result === 'Positivo' ? 'default' : 'secondary'}>
                  {record.marker_result}
                </Badge>
              )}
            </td>
            <td className="p-3">
              <div className="text-sm text-muted-foreground">
                {record.intensity || '–'}
              </div>
            </td>
            <td className="p-3">
              <div className="text-sm text-muted-foreground">
                {record.percentage !== null && record.percentage !== undefined ? `${record.percentage}%` : '–'}
              </div>
            </td>
            <td className="p-3 text-right">
              <div className="flex gap-1 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingIndex({ section: 'immunohistochemistry', index: idx })}
                  className="h-7 px-2"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir este registro?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete('immunohistochemistry', idx)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Função para renderizar linha de tabela de métricas
  const renderMetricsRow = (record: BiopsyRecord, idx: number, dateKey: string) => {
    const isEditing = editingIndex?.section === 'metrics' && editingIndex?.index === idx;
    const editedRecord = editedRecords.get('metrics')?.[idx] || record;

    return (
      <React.Fragment key={`${dateKey}-metrics-${idx}`}>
        {isEditing ? (
          <tr className="border-b border-border">
            <td colSpan={5} className="p-3 bg-muted/30">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Nome da Métrica</Label>
                    <Input
                      value={editedRecord.metric_name || ''}
                      onChange={(e) => updateEditedRecord('metrics', idx, 'metric_name', e.target.value || null)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Valor</Label>
                    <Input
                      type="number"
                      step="any"
                      value={editedRecord.value ?? ''}
                      onChange={(e) => updateEditedRecord('metrics', idx, 'value', e.target.value ? parseFloat(e.target.value) : null)}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Unidade</Label>
                    <Input
                      value={editedRecord.unit || ''}
                      onChange={(e) => updateEditedRecord('metrics', idx, 'unit', e.target.value || null)}
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => handleSave('metrics', idx)} disabled={isSaving}>
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingIndex(null)} disabled={isSaving}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : (
          <tr className="border-b border-border hover:bg-muted/30 transition-colors">
            <td className="p-3 text-sm text-muted-foreground">
              {record.date && !isNaN(new Date(record.date).getTime())
                ? format(new Date(record.date), "dd/MM/yyyy")
                : '–'}
            </td>
            <td className="p-3">
              <div className="text-sm font-medium">
                {record.metric_name || '–'}
              </div>
            </td>
            <td className="p-3">
              <div className="text-sm font-semibold">
                {record.value !== null && record.value !== undefined ? record.value : '–'}
              </div>
            </td>
            <td className="p-3">
              <div className="text-sm text-muted-foreground">
                {record.unit || '–'}
              </div>
            </td>
            <td className="p-3 text-right">
              <div className="flex gap-1 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingIndex({ section: 'metrics', index: idx })}
                  className="h-7 px-2"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir este registro?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete('metrics', idx)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  if (allBiopsyRecords.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground bg-background border-t">
        <p className="text-sm">Nenhum registro de biópsia encontrado nesta sessão</p>
      </div>
    );
  }

  return (
    <div className="bg-background border-t">

      {/* Achados Macroscópicos */}
      {macroscopicMainRecord && macroscopicSubRecords.length > 0 && (
        <div className="px-4 py-3 border-b border-border">
          <div className="mb-3 flex items-center gap-2">
            <Search className="h-4 w-4 text-primary" />
            <span className="font-semibold text-sm">Achados Macroscópicos</span>
            <Badge variant="secondary" className="ml-2">
              {macroscopicSubRecords.length}
            </Badge>
          </div>
          {isMobile ? (
            <div className="space-y-3">
              {(editedRecords.get('macroscopic') || macroscopicSubRecords).map((record, idx) => {
                const isEditing = editingIndex?.section === 'macroscopic' && editingIndex?.index === idx;
                const editedRecord = editedRecords.get('macroscopic')?.[idx] || record;
                return (
                  <Card key={idx} className="border-l-4 border-l-primary">
                    <CardContent className="p-4 space-y-3">
                      {isEditing ? (
                        <>
                          <div>
                            <Label className="text-xs">Descrição</Label>
                            <Textarea
                              value={editedRecord.finding_description || ''}
                              onChange={(e) => updateEditedRecord('macroscopic', idx, 'finding_description', e.target.value || null)}
                              className="min-h-[80px]"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Tipo</Label>
                              <Input
                                value={editedRecord.finding_type || ''}
                                onChange={(e) => updateEditedRecord('macroscopic', idx, 'finding_type', e.target.value || null)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Dimensões</Label>
                              <Input
                                value={editedRecord.dimensions || ''}
                                onChange={(e) => updateEditedRecord('macroscopic', idx, 'dimensions', e.target.value || null)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Cor</Label>
                              <Input
                                value={editedRecord.color || ''}
                                onChange={(e) => updateEditedRecord('macroscopic', idx, 'color', e.target.value || null)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Consistência</Label>
                              <Input
                                value={editedRecord.consistency || ''}
                                onChange={(e) => updateEditedRecord('macroscopic', idx, 'consistency', e.target.value || null)}
                                className="h-8"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2 pt-2">
                            <Button size="sm" onClick={() => handleSave('macroscopic', idx)} disabled={isSaving}>
                              {isSaving ? "Salvando..." : "Salvar"}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingIndex(null)} disabled={isSaving}>
                              Cancelar
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-start justify-between">
                            <div className="flex-1 space-y-2">
                              {record.finding_description && (
                                <p className="text-sm">{record.finding_description}</p>
                              )}
                              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                                {record.finding_type && <p><span className="font-semibold">Tipo:</span> {record.finding_type}</p>}
                                {record.dimensions && <p><span className="font-semibold">Dimensões:</span> {record.dimensions}</p>}
                                {record.color && <p><span className="font-semibold">Cor:</span> {record.color}</p>}
                                {record.consistency && <p><span className="font-semibold">Consistência:</span> {record.consistency}</p>}
                              </div>
                            </div>
                            <div className="flex gap-1 ml-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingIndex({ section: 'macroscopic', index: idx })}
                                className="h-7 px-2"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Tem certeza que deseja excluir este registro?
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete('macroscopic', idx)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Excluir
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '250px' }} />
                  <col style={{ width: '150px' }} />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '180px' }} />
                  <col style={{ width: '120px' }} />
                </colgroup>
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Data
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Descrição
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Tipo
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Dimensões
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Cor / Consistência
                    </th>
                    <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const records = editedRecords.get('macroscopic') || macroscopicSubRecords;
                    const grouped = groupRecordsByDate(records);
                    const sortedDates = sortDateKeys(Object.keys(grouped));
                    
                    return sortedDates.map((dateKey) => {
                      const dateGroup = grouped[dateKey];
                      const dateRecords = dateGroup.records;
                      const displayKey = dateGroup.displayKey;
                      const uniqueKey = `macroscopic-${dateKey}`;
                      const isExpanded = expandedDateGroups.get(uniqueKey) ?? expandAllByDefault;

                      return (
                        <React.Fragment key={dateKey}>
                          <tr className="bg-gray-100/50 border-y-2 border-gray-200">
                            <td colSpan={6} className="p-2">
                              <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroup(uniqueKey, open)}>
                                <CollapsibleTrigger asChild>
                                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 rounded px-2 py-1 transition-colors w-full">
                                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    <Search className="h-4 w-4" />
                                    <span>📅 {displayKey}</span>
                                    <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                                    <Badge variant="secondary">
                                      {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                                    </Badge>
                                  </div>
                                </CollapsibleTrigger>
                              </Collapsible>
                            </td>
                          </tr>
                          {isExpanded && (
                            <>
                              {dateRecords.map((record, groupIndex) => {
                                const originalIndex = record._index ?? groupIndex;
                                return renderMacroscopicRow(record, originalIndex, dateKey);
                              })}
                            </>
                          )}
                        </React.Fragment>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Achados Microscópicos */}
      {microscopicMainRecord && microscopicSubRecords.length > 0 && (
        <div className="px-4 py-3 border-b border-border">
          <div className="mb-3 flex items-center gap-2">
            <Microscope className="h-4 w-4 text-primary" />
            <span className="font-semibold text-sm">Achados Microscópicos</span>
            <Badge variant="secondary" className="ml-2">
              {microscopicSubRecords.length}
            </Badge>
          </div>
          {isMobile ? (
            <div className="space-y-3">
              {(editedRecords.get('microscopic') || microscopicSubRecords).map((record, idx) => {
                const isEditing = editingIndex?.section === 'microscopic' && editingIndex?.index === idx;
                const editedRecord = editedRecords.get('microscopic')?.[idx] || record;
                return (
                  <Card key={idx} className="border-l-4 border-l-primary">
                    <CardContent className="p-4 space-y-3">
                      {isEditing ? (
                        <>
                          <div>
                            <Label className="text-xs">Descrição</Label>
                            <Textarea
                              value={editedRecord.finding_description || ''}
                              onChange={(e) => updateEditedRecord('microscopic', idx, 'finding_description', e.target.value || null)}
                              className="min-h-[80px]"
                            />
                          </div>
                          <div className="grid grid-cols-1 gap-3">
                            <div>
                              <Label className="text-xs">Padrão Histológico</Label>
                              <Input
                                value={editedRecord.histological_pattern || ''}
                                onChange={(e) => updateEditedRecord('microscopic', idx, 'histological_pattern', e.target.value || null)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Características Celulares</Label>
                              <Input
                                value={editedRecord.cellular_features || ''}
                                onChange={(e) => updateEditedRecord('microscopic', idx, 'cellular_features', e.target.value || null)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Características do Estroma</Label>
                              <Input
                                value={editedRecord.stromal_features || ''}
                                onChange={(e) => updateEditedRecord('microscopic', idx, 'stromal_features', e.target.value || null)}
                                className="h-8"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2 pt-2">
                            <Button size="sm" onClick={() => handleSave('microscopic', idx)} disabled={isSaving}>
                              {isSaving ? "Salvando..." : "Salvar"}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingIndex(null)} disabled={isSaving}>
                              Cancelar
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-start justify-between">
                            <div className="flex-1 space-y-2">
                              {record.finding_description && (
                                <p className="text-sm">{record.finding_description}</p>
                              )}
                              <div className="grid grid-cols-1 gap-2 text-xs text-muted-foreground">
                                {record.histological_pattern && <p><span className="font-semibold">Padrão Histológico:</span> {record.histological_pattern}</p>}
                                {record.cellular_features && <p><span className="font-semibold">Características Celulares:</span> {record.cellular_features}</p>}
                                {record.stromal_features && <p><span className="font-semibold">Características do Estroma:</span> {record.stromal_features}</p>}
                              </div>
                            </div>
                            <div className="flex gap-1 ml-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingIndex({ section: 'microscopic', index: idx })}
                                className="h-7 px-2"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Tem certeza que deseja excluir este registro?
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete('microscopic', idx)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Excluir
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '250px' }} />
                  <col style={{ width: '200px' }} />
                  <col style={{ width: '200px' }} />
                  <col style={{ width: '120px' }} />
                </colgroup>
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Data
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Descrição
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Padrão Histológico
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Características
                    </th>
                    <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const records = editedRecords.get('microscopic') || microscopicSubRecords;
                    const grouped = groupRecordsByDate(records);
                    const sortedDates = sortDateKeys(Object.keys(grouped));
                    
                    return sortedDates.map((dateKey) => {
                      const dateGroup = grouped[dateKey];
                      const dateRecords = dateGroup.records;
                      const displayKey = dateGroup.displayKey;
                      const uniqueKey = `microscopic-${dateKey}`;
                      const isExpanded = expandedDateGroups.get(uniqueKey) ?? expandAllByDefault;

                      return (
                        <React.Fragment key={dateKey}>
                          <tr className="bg-gray-100/50 border-y-2 border-gray-200">
                            <td colSpan={5} className="p-2">
                              <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroup(uniqueKey, open)}>
                                <CollapsibleTrigger asChild>
                                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 rounded px-2 py-1 transition-colors w-full">
                                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    <Microscope className="h-4 w-4" />
                                    <span>📅 {displayKey}</span>
                                    <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                                    <Badge variant="secondary">
                                      {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                                    </Badge>
                                  </div>
                                </CollapsibleTrigger>
                              </Collapsible>
                            </td>
                          </tr>
                          {isExpanded && (
                            <>
                              {dateRecords.map((record, groupIndex) => {
                                const originalIndex = record._index ?? groupIndex;
                                return renderMicroscopicRow(record, originalIndex, dateKey);
                              })}
                            </>
                          )}
                        </React.Fragment>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Diagnóstico */}
      {diagnosisMainRecord && diagnosisSubRecords.length > 0 && (
        <div className="px-4 py-3 border-b border-border">
          <div className="mb-3 flex items-center gap-2">
            <Stethoscope className="h-4 w-4 text-primary" />
            <span className="font-semibold text-sm">Diagnóstico</span>
            <Badge variant="secondary" className="ml-2">
              {diagnosisSubRecords.length}
            </Badge>
          </div>
          {isMobile ? (
            <div className="space-y-3">
              {(editedRecords.get('diagnosis') || diagnosisSubRecords).map((record, idx) => {
                const isEditing = editingIndex?.section === 'diagnosis' && editingIndex?.index === idx;
                const editedRecord = editedRecords.get('diagnosis')?.[idx] || record;
                return (
                  <Card key={idx} className="border-l-4 border-l-primary">
                    <CardContent className="p-4 space-y-3">
                      {isEditing ? (
                        <>
                          <div>
                            <Label className="text-xs">Diagnóstico</Label>
                            <Textarea
                              value={editedRecord.diagnosis_text || ''}
                              onChange={(e) => updateEditedRecord('diagnosis', idx, 'diagnosis_text', e.target.value || null)}
                              className="min-h-[80px]"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Tipo</Label>
                              <Input
                                value={editedRecord.diagnosis_type || ''}
                                onChange={(e) => updateEditedRecord('diagnosis', idx, 'diagnosis_type', e.target.value || null)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Local</Label>
                              <Input
                                value={editedRecord.site || ''}
                                onChange={(e) => updateEditedRecord('diagnosis', idx, 'site', e.target.value || null)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Grau</Label>
                              <Input
                                value={editedRecord.grade || ''}
                                onChange={(e) => updateEditedRecord('diagnosis', idx, 'grade', e.target.value || null)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Estadiamento</Label>
                              <Input
                                value={editedRecord.stage || ''}
                                onChange={(e) => updateEditedRecord('diagnosis', idx, 'stage', e.target.value || null)}
                                className="h-8"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2 pt-2">
                            <Button size="sm" onClick={() => handleSave('diagnosis', idx)} disabled={isSaving}>
                              {isSaving ? "Salvando..." : "Salvar"}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingIndex(null)} disabled={isSaving}>
                              Cancelar
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-start justify-between">
                            <div className="flex-1 space-y-2">
                              {record.diagnosis_text && (
                                <p className="text-sm font-medium">{record.diagnosis_text}</p>
                              )}
                              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                                {record.diagnosis_type && <p><span className="font-semibold">Tipo:</span> {record.diagnosis_type}</p>}
                                {record.site && <p><span className="font-semibold">Local:</span> {record.site}</p>}
                                {record.grade && <p><span className="font-semibold">Grau:</span> {record.grade}</p>}
                                {record.stage && <p><span className="font-semibold">Estadiamento:</span> {record.stage}</p>}
                              </div>
                            </div>
                            <div className="flex gap-1 ml-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingIndex({ section: 'diagnosis', index: idx })}
                                className="h-7 px-2"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Tem certeza que deseja excluir este registro?
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete('diagnosis', idx)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Excluir
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '250px' }} />
                  <col style={{ width: '150px' }} />
                  <col style={{ width: '150px' }} />
                  <col style={{ width: '150px' }} />
                  <col style={{ width: '120px' }} />
                </colgroup>
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Data
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Diagnóstico
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Tipo
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Local
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Grau / Estadiamento
                    </th>
                    <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const records = editedRecords.get('diagnosis') || diagnosisSubRecords;
                    const grouped = groupRecordsByDate(records);
                    const sortedDates = sortDateKeys(Object.keys(grouped));
                    
                    return sortedDates.map((dateKey) => {
                      const dateGroup = grouped[dateKey];
                      const dateRecords = dateGroup.records;
                      const displayKey = dateGroup.displayKey;
                      const uniqueKey = `diagnosis-${dateKey}`;
                      const isExpanded = expandedDateGroups.get(uniqueKey) ?? expandAllByDefault;

                      return (
                        <React.Fragment key={dateKey}>
                          <tr className="bg-gray-100/50 border-y-2 border-gray-200">
                            <td colSpan={6} className="p-2">
                              <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroup(uniqueKey, open)}>
                                <CollapsibleTrigger asChild>
                                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 rounded px-2 py-1 transition-colors w-full">
                                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    <Stethoscope className="h-4 w-4" />
                                    <span>📅 {displayKey}</span>
                                    <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                                    <Badge variant="secondary">
                                      {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                                    </Badge>
                                  </div>
                                </CollapsibleTrigger>
                              </Collapsible>
                            </td>
                          </tr>
                          {isExpanded && (
                            <>
                              {dateRecords.map((record, groupIndex) => {
                                const originalIndex = record._index ?? groupIndex;
                                return renderDiagnosisRow(record, originalIndex, dateKey);
                              })}
                            </>
                          )}
                        </React.Fragment>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Imunohistoquímica */}
      {immunohistochemistryMainRecord && immunohistochemistrySubRecords.length > 0 && (
        <div className="px-4 py-3 border-b border-border">
          <div className="mb-3 flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-primary" />
            <span className="font-semibold text-sm">Imunohistoquímica</span>
            <Badge variant="secondary" className="ml-2">
              {immunohistochemistrySubRecords.length}
            </Badge>
          </div>
          {isMobile ? (
            <div className="space-y-3">
              {(editedRecords.get('immunohistochemistry') || immunohistochemistrySubRecords).map((record, idx) => {
                const isEditing = editingIndex?.section === 'immunohistochemistry' && editingIndex?.index === idx;
                const editedRecord = editedRecords.get('immunohistochemistry')?.[idx] || record;
                return (
                  <Card key={idx} className="border-l-4 border-l-primary">
                    <CardContent className="p-4 space-y-3">
                      {isEditing ? (
                        <>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Marcador</Label>
                              <Input
                                value={editedRecord.marker_name || ''}
                                onChange={(e) => updateEditedRecord('immunohistochemistry', idx, 'marker_name', e.target.value || null)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Resultado</Label>
                              <Select
                                value={editedRecord.marker_result || ''}
                                onValueChange={(value) => updateEditedRecord('immunohistochemistry', idx, 'marker_result', value || null)}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Positivo">Positivo</SelectItem>
                                  <SelectItem value="Negativo">Negativo</SelectItem>
                                  <SelectItem value="Fracamente Positivo">Fracamente Positivo</SelectItem>
                                  <SelectItem value="Moderadamente Positivo">Moderadamente Positivo</SelectItem>
                                  <SelectItem value="Fortemente Positivo">Fortemente Positivo</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Intensidade</Label>
                              <Input
                                value={editedRecord.intensity || ''}
                                onChange={(e) => updateEditedRecord('immunohistochemistry', idx, 'intensity', e.target.value || null)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Percentual (%)</Label>
                              <Input
                                type="number"
                                value={editedRecord.percentage ?? ''}
                                onChange={(e) => updateEditedRecord('immunohistochemistry', idx, 'percentage', e.target.value ? parseFloat(e.target.value) : null)}
                                className="h-8"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2 pt-2">
                            <Button size="sm" onClick={() => handleSave('immunohistochemistry', idx)} disabled={isSaving}>
                              {isSaving ? "Salvando..." : "Salvar"}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingIndex(null)} disabled={isSaving}>
                              Cancelar
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-start justify-between">
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center justify-between">
                                {record.marker_name && (
                                  <p className="text-sm font-medium">{record.marker_name}</p>
                                )}
                                {record.marker_result && (
                                  <Badge variant={record.marker_result === 'Positivo' ? 'default' : 'secondary'}>
                                    {record.marker_result}
                                  </Badge>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                                {record.intensity && <p><span className="font-semibold">Intensidade:</span> {record.intensity}</p>}
                                {record.percentage !== null && record.percentage !== undefined && (
                                  <p><span className="font-semibold">Percentual:</span> {record.percentage}%</p>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1 ml-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingIndex({ section: 'immunohistochemistry', index: idx })}
                                className="h-7 px-2"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Tem certeza que deseja excluir este registro?
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete('immunohistochemistry', idx)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Excluir
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '200px' }} />
                  <col style={{ width: '150px' }} />
                  <col style={{ width: '150px' }} />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '120px' }} />
                </colgroup>
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Data
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Marcador
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Resultado
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Intensidade
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Percentual
                    </th>
                    <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const records = editedRecords.get('immunohistochemistry') || immunohistochemistrySubRecords;
                    const grouped = groupRecordsByDate(records);
                    const sortedDates = sortDateKeys(Object.keys(grouped));
                    
                    return sortedDates.map((dateKey) => {
                      const dateGroup = grouped[dateKey];
                      const dateRecords = dateGroup.records;
                      const displayKey = dateGroup.displayKey;
                      const uniqueKey = `immunohistochemistry-${dateKey}`;
                      const isExpanded = expandedDateGroups.get(uniqueKey) ?? expandAllByDefault;

                      return (
                        <React.Fragment key={dateKey}>
                          <tr className="bg-gray-100/50 border-y-2 border-gray-200">
                            <td colSpan={6} className="p-2">
                              <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroup(uniqueKey, open)}>
                                <CollapsibleTrigger asChild>
                                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 rounded px-2 py-1 transition-colors w-full">
                                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    <FlaskConical className="h-4 w-4" />
                                    <span>📅 {displayKey}</span>
                                    <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                                    <Badge variant="secondary">
                                      {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                                    </Badge>
                                  </div>
                                </CollapsibleTrigger>
                              </Collapsible>
                            </td>
                          </tr>
                          {isExpanded && (
                            <>
                              {dateRecords.map((record, groupIndex) => {
                                const originalIndex = record._index ?? groupIndex;
                                return renderImmunohistochemistryRow(record, originalIndex, dateKey);
                              })}
                            </>
                          )}
                        </React.Fragment>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Métricas */}
      {metricsRecords.length > 0 && (
        <div className="px-4 py-3 border-b border-border">
          <div className="mb-3 flex items-center gap-2">
            <Ruler className="h-4 w-4 text-primary" />
            <span className="font-semibold text-sm">Métricas</span>
            <Badge variant="secondary" className="ml-2">
              {metricsRecords.length}
            </Badge>
          </div>
          {isMobile ? (
            <div className="space-y-3">
              {(editedRecords.get('metrics') || metricsRecords).map((record, idx) => {
                const isEditing = editingIndex?.section === 'metrics' && editingIndex?.index === idx;
                const editedRecord = editedRecords.get('metrics')?.[idx] || record;
                return (
                  <Card key={idx} className="border-l-4 border-l-primary">
                    <CardContent className="p-4 space-y-3">
                      {isEditing ? (
                        <>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Nome da Métrica</Label>
                              <Input
                                value={editedRecord.metric_name || ''}
                                onChange={(e) => updateEditedRecord('metrics', idx, 'metric_name', e.target.value || null)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Valor</Label>
                              <Input
                                type="number"
                                step="any"
                                value={editedRecord.value ?? ''}
                                onChange={(e) => updateEditedRecord('metrics', idx, 'value', e.target.value ? parseFloat(e.target.value) : null)}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Unidade</Label>
                              <Input
                                value={editedRecord.unit || ''}
                                onChange={(e) => updateEditedRecord('metrics', idx, 'unit', e.target.value || null)}
                                className="h-8"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2 pt-2">
                            <Button size="sm" onClick={() => handleSave('metrics', idx)} disabled={isSaving}>
                              {isSaving ? "Salvando..." : "Salvar"}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingIndex(null)} disabled={isSaving}>
                              Cancelar
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              {record.metric_name && (
                                <p className="text-sm font-medium">{record.metric_name}</p>
                              )}
                              {record.value !== null && record.value !== undefined && (
                                <p className="text-sm font-semibold">
                                  {record.value} {record.unit || ''}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-1 ml-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingIndex({ section: 'metrics', index: idx })}
                                className="h-7 px-2"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Tem certeza que deseja excluir este registro?
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete('metrics', idx)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Excluir
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '250px' }} />
                  <col style={{ width: '150px' }} />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '120px' }} />
                </colgroup>
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Data
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Nome da Métrica
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Valor
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Unidade
                    </th>
                    <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const records = editedRecords.get('metrics') || metricsRecords;
                    const grouped = groupRecordsByDate(records);
                    const sortedDates = sortDateKeys(Object.keys(grouped));
                    
                    return sortedDates.map((dateKey) => {
                      const dateGroup = grouped[dateKey];
                      const dateRecords = dateGroup.records;
                      const displayKey = dateGroup.displayKey;
                      const uniqueKey = `metrics-${dateKey}`;
                      const isExpanded = expandedDateGroups.get(uniqueKey) ?? expandAllByDefault;

                      return (
                        <React.Fragment key={dateKey}>
                          <tr className="bg-gray-100/50 border-y-2 border-gray-200">
                            <td colSpan={5} className="p-2">
                              <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroup(uniqueKey, open)}>
                                <CollapsibleTrigger asChild>
                                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 rounded px-2 py-1 transition-colors w-full">
                                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    <Ruler className="h-4 w-4" />
                                    <span>📅 {displayKey}</span>
                                    <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                                    <Badge variant="secondary">
                                      {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                                    </Badge>
                                  </div>
                                </CollapsibleTrigger>
                              </Collapsible>
                            </td>
                          </tr>
                          {isExpanded && (
                            <>
                              {dateRecords.map((record, groupIndex) => {
                                const originalIndex = record._index ?? groupIndex;
                                return renderMetricsRow(record, originalIndex, dateKey);
                              })}
                            </>
                          )}
                        </React.Fragment>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="p-3 text-xs text-muted-foreground border-t border-border bg-muted/30">
        Total: {allBiopsyRecords.filter(r => !r.is_main_record).length} registro(s) de biópsia
      </div>
    </div>
  );
}

